import Loadable from 'react-loadable';
import React from 'react';
import { Router } from '@reach/router';
import { _cs } from '@togglecorp/fujs';

import Button from '#rsca/Button';
import DangerButton from '#rsca/Button/DangerButton';

import ViewContext from '#components/ViewContext';
import LoadingAnimation from '#rscv/LoadingAnimation';
import Message from '#rscv/Message';
import AppBar from '#components/AppBar';
import errorBound from '#components/errorBound';
import helmetify from '#components/helmetify';
import withAuth from '#components/withAuth';
import { NotifyMessage } from '#components/notify';

import { routeSettings, hasAppBar } from '#constants';
import * as types from '#constants/types';

import styles from './styles.scss';

interface LoadOptions {
    error: string;
    retry: () => void;
}

function reloadPage(): void {
    window.location.reload(false);
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const ErrorInPage = () => (
    <Message className={styles.pageError}>
        <div className={styles.message}>
            Some problem occurred.
        </div>
        <DangerButton
            className={styles.reloadButton}
            transparent
            onClick={reloadPage}
        >
            Reload
        </DangerButton>
    </Message>
);

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const RetryableErrorInPage = ({ error, retry }: LoadOptions) => {
    console.error(error);
    return (
        <Message className={styles.retryablePageError}>
            <div className={styles.message}>
                Some problem occurred.
            </div>
            <DangerButton
                className={styles.reloadButton}
                onClick={retry}
                transparent
            >
                Reload
            </DangerButton>
        </Message>
    );
};

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const LoadingPage = ({ error, retry }: LoadOptions) => {
    if (error) {
        return (
            <RetryableErrorInPage
                error={error}
                retry={retry}
            />
        );
    }

    return (
        <LoadingAnimation message="Loading page" />
    );
};

const routes = routeSettings.map(({ load, props, ...settings }) => {
    type ExtendedSettings = typeof settings & { className?: string };

    const Component = errorBound<ExtendedSettings>(ErrorInPage)(
        withAuth(
            helmetify(
                Loadable({
                    loader: load,
                    loading: LoadingPage,
                }),
            ),
        ),
    );

    return (
        <Component
            key={settings.name}
            className={styles.content}
            {...settings}
            {...props}
        />
    );
});

const EmptyComponent = () => null;

const appBars = routeSettings.map((settings) => {
    const { name, title, ...otherProps } = settings;

    if (!hasAppBar(settings)) {
        return <EmptyComponent key={name} {...otherProps} />;
    }

    return (
        <AppBar
            key={name}
            className={styles.appBar}
            title={title}
            {...otherProps}
        />
    );
});

interface State {
    showBackButton?: boolean;
    appBarTitle?: string;
    message?: string;
    messageType?: string;
    appBarChildren?: React.ReactNode;
}

interface Props {
    pending: boolean;
    logoutPending: boolean;
    className?: string;
    isAuthenticated?: boolean;
    userDetail?: types.UserDetail;
}

class Multiplexer extends React.PureComponent<Props, State> {
    public constructor(props: Props) {
        super(props);

        this.state = {
            showBackButton: undefined,
            appBarTitle: undefined,
            message: undefined,
            messageType: undefined,
            appBarChildren: undefined,
        };
    }

    public componentDidMount() {
        document.addEventListener('notify', this.handleNotify);
    }

    public componentWillUnmount() {
        document.removeEventListener('notify', this.handleNotify);
    }

    private setShowBackButton = (showBackButton: boolean) => {
        this.setState({ showBackButton });
    }

    private setAppBarTitle = (appBarTitle?: string) => {
        this.setState({ appBarTitle });
    }

    private setAppBarChildren = (appBarChildren?: React.ReactNode) => {
        this.setState({ appBarChildren });
    }

    private handleNotify = ({ detail }: NotifyMessage) => {
        this.setState({
            message: detail.message,
            messageType: detail.type,
        });
    }

    private handleCloseButtonClick = () => {
        this.setState({
            message: undefined,
            messageType: undefined,
        });
    }

    public render() {
        const {
            className,
            pending,
            logoutPending,
            userDetail,
        } = this.props;

        const {
            showBackButton,
            appBarTitle,
            message,
            messageType,
            appBarChildren,
        } = this.state;

        const viewProps = {
            setShowBackButton: this.setShowBackButton,
            setAppBarTitle: this.setAppBarTitle,
            setAppBarChildren: this.setAppBarChildren,
            showBackButton,
            appBarTitle,
            appBarChildren,
            userDetail,
        };

        const notifyClassName = message
            ? _cs(
                styles.notify,
                messageType === 'error' && styles.errorMessage,
                messageType === 'success' && styles.successMessage,
            )
            : undefined;

        return (
            <div className={_cs(styles.multiplexer, className, 'multiplexer')}>
                {(pending || logoutPending) ? (
                    <LoadingAnimation
                        message={logoutPending ? 'Logging out' : 'Loading resources'}
                    />
                ) : (
                    <ViewContext.Provider value={viewProps}>
                        <Router className={styles.appBarContainer}>
                            {appBars}
                        </Router>
                        { message && (
                            <div className={notifyClassName}>
                                <div className={styles.message}>
                                    { message }
                                </div>
                                <Button
                                    transparent
                                    className={styles.closeButton}
                                    iconName="close"
                                    onClick={this.handleCloseButtonClick}
                                />
                            </div>
                        )}
                        <Router className={styles.router}>
                            {routes}
                        </Router>
                    </ViewContext.Provider>
                )}
            </div>
        );
    }
}

export default Multiplexer;
